import CancelIcon from '@mui/icons-material/Cancel';
import {
  Box,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ThemePalette } from 'mui.theme';
import React, { useEffect, useState } from 'react';
import {
  type AssetsOverview,
  type CumulativeOverview,
} from 'types/survivalAnalysis.types';
import {
  categoryMapping,
  filterByCategoryBySubCategory,
  generateGraphData,
} from '../Function/helper.function';
import { SurvivalAnalysisBarChart } from './SurvivalAnalysis.ProgressBarChart';
import { SurvivalAnalysisProgressDotLine } from './SurvivalAnalysis.ProgressDotLineChart';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const SurvivalAnalysisDetailsChart = ({
  enableDarkTheme = false,
  workOrderAgeCategory = 'low',
  assetsPerSubComponent,
  component,
}: {
  enableDarkTheme: boolean;
  workOrderAgeCategory?: 'high' | 'med' | 'low' | 'eol';
  assetsPerSubComponent: CumulativeOverview[];
  component: AssetsOverview;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const [generateGraphDataItems, setGenerateGraphDataItems] = useState<any[]>(
    []
  );
  const [filterAssets, setFilterAssets] = useState<any[]>([]);
  const [chartType, setChartType] = useState<'ABS' | 'COM'>('COM'); // Add state for chart type

  useEffect(() => {
    const filteredAssets = filterByCategoryBySubCategory(
      assetsPerSubComponent,
      component.components_category,
      component.sub_component
    );
    setFilterAssets(filteredAssets);
    setGenerateGraphDataItems(generateGraphData(filteredAssets));
  }, [
    assetsPerSubComponent,
    component.components_category,
    component.sub_component,
  ]);

  const handleChartTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newChartType: 'ABS' | 'COM' | null
  ) => {
    if (newChartType !== null) {
      setChartType(newChartType);
    }
  };

  return (
    <Box
      sx={{
        padding: 1,
        width: '100%',
        maxWidth: 1200, // Adjust as needed
        height: 220, // Adjust as needed
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          variant="body2"
          sx={{
            ml: 1,
            color: palette.textPrimary,
          }}
        >
          {t('current_status')}
        </Typography>
        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={handleChartTypeChange}
          aria-label={t('chart_type')}
          size="small"
          sx={{ mr: 1 }}
        >
          <ToggleButton
            value="ABS"
            aria-label={t('dot_line_chart')}
            sx={{
              minWidth: 40,
              padding: '1px 7px',
              fontSize: '0.5rem',
              color: chartType === 'ABS' ? 'darkgrey' : 'lightgrey',
              '&.Mui-selected': {
                backgroundColor: 'lightgrey',
                color: 'black',
              },
              '&:hover': {
                backgroundColor: chartType === 'ABS' ? 'lightgrey' : 'darkgrey',
              },
            }}
          >
            ABS
          </ToggleButton>
          <ToggleButton
            value="COM"
            aria-label={t('bar_chart')}
            sx={{
              minWidth: 40,
              padding: '1px 7px',
              fontSize: '0.5rem',
              color: chartType === 'COM' ? 'darkgrey' : 'lightgrey',
              '&.Mui-selected': {
                backgroundColor: 'lightgrey',
                color: 'black',
              },
              '&:hover': {
                backgroundColor: chartType === 'COM' ? 'lightgrey' : 'darkgrey',
              },
            }}
          >
            COM
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          sx={{ color: categoryMapping[workOrderAgeCategory].color ?? 'black' }}
        >
          <CancelIcon />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: categoryMapping[workOrderAgeCategory].color }}
          >
            {categoryMapping[workOrderAgeCategory].text}
          </Typography>
        </Box>
      </Box>

      {/* Conditionally render the selected chart */}
      {chartType === 'ABS' ? (
        <SurvivalAnalysisProgressDotLine
          value={generateGraphDataItems}
          enableDarkTheme={enableDarkTheme}
        />
      ) : (
        <SurvivalAnalysisBarChart
          generateGraphDataItems={generateGraphDataItems}
          component={component}
          enableDarkTheme={enableDarkTheme}
          // pointerColor={categoryMapping[workOrderAgeCategory].color}
          pointerColor={
            enableDarkTheme
              ? ThemePalette.light.topBar
              : ThemePalette.dark.topBar
          }
        />
      )}
    </Box>
  );
};
